import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { CheckCircle, Home } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import axios from "@/lib/axios";
import { API_BASE_URL } from '@/config';
import { useLoader } from '@/contexts/LoaderContext';
import JourneyHeader from "@/components/JourneyHeader";

const LoanSuccess = ({ isBuilderMode = false }: { isBuilderMode?: boolean }) => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const [instructions, setInstructions] = useState<string | null>(null);

  const handleExit = () => {
    navigate('/home');
  };

  useEffect(() => {
    if (isBuilderMode) return;
    const fetchInstructions = async () => {
      setLoading(true);
      try {
        const response = await axios.post(`${API_BASE_URL}/api/journey/success_instructions/`);
        if (response.data && response.data.instructions) {
          setInstructions(response.data.instructions);
        }
      } catch (error) {
        console.error("Failed to fetch success instructions", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInstructions();
  }, [isBuilderMode, setLoading]);

  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🎉</div>
        <p className="font-semibold text-gray-500">Loan Success</p>
        <p className="text-xs mt-1">Full-screen loan success / congratulations page shown after application is approved.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-intellend-white">
      {/* Header with centered logo */}
      <JourneyHeader />

      {/* Main content */}
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="bg-white shadow-md rounded-lg p-6 mb-8">
          <div className="flex justify-center mb-4">
            <CheckCircle className="text-intellend-green h-20 w-20" />
          </div>

          <h2 className="text-2xl font-bold text-intellend-green mb-6 text-center">
            Congratulations!!!
          </h2>

          {instructions ? (
            <div
              className="dynamic-instructions"
              dangerouslySetInnerHTML={{ __html: instructions }}
            />
          ) : (
            <div className="text-center mb-8">
              <p className="mb-4 text-lg">
                Your Intellend loan application has been successfully accepted.
              </p>
              <p className="mb-4 text-lg">
                You will soon receive an email / call from us for the next steps.
              </p>
              <p className="mb-4 text-lg text-gray-500 italic">
                Loading detailed instructions...
              </p>
            </div>
          )}

          <Card className="bg-gray-50 my-8">
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 text-center">
                If you have any questions, please write back to us at <a href="mailto:support@intellendtech.com" className="text-intellend-green font-medium">support@intellendtech.com</a>
              </p>
            </CardContent>
          </Card>

          <div className="flex justify-center mt-8">
            <Button
              onClick={handleExit}
              className="flex items-center gap-2"
            >
              <Home size={18} />
              Exit to Home
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoanSuccess;
